import { useState, useCallback } from "react";
import { Button } from "reactstrap";
import { PlusIcon } from "@heroicons/react/24/outline";

import DataGrid from "components/DataGrid";
import { documentColDefs } from "../constants/documentColDefs";
import { CreateDocumentModal } from "./CreateDocumentModal";
import { EditDocumentModal } from "./EditDocumentModal";
import { apiSlice } from "@/app/apiSlice";

interface DocumentListProps {
  filterParams?: any;
  className?: string;
  gridId?: string;
  showCreateButton?: boolean;
  excludeDataSources?: string;
  creatorId?: string;
  campaignId?: string;
}

export const DocumentList = ({
  filterParams = {},
  className,
  gridId = "",
  showCreateButton = true,
  excludeDataSources = "",
  creatorId,
  campaignId,
}: DocumentListProps) => {
  const {
    data: documents,
    isFetching,
    isLoading,
    refetch,
  } = apiSlice.useGetDocumentsQuery(filterParams);

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleDocumentClick = useCallback(({ _id }: { _id: string }) => {
    setSelectedDocumentId(_id);
    setIsEditModalOpen(true);
  }, []);

  const handleCreateClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  const handleDocumentSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  const id = (gridId += ".documents");

  return (
    <div
      className={`documents-grid d-flex flex-column h-100 ${className || ""}`}
    >
      {/* Documents Grid */}
      <div className="flex-grow-1 d-flex flex-column">
        <DataGrid
          appliedFilters={true}
          autoSizeEnabled={true}
          autoSizeIgnoreHeaders={false}
          autoSizeStrategy={{ type: "fitGridWidth" }}
          autoHeight={false}
          borders={true}
          columnDefs={documentColDefs}
          excludeDataSources={excludeDataSources}
          gridId={gridId}
          idField="_id"
          isLoading={isLoading}
          isFetching={isFetching}
          refetch={refetch}
          quickFilter={true}
          rowData={documents || []}
          actionText="View"
          actionClick={handleDocumentClick}
          HeaderActionElem={
            showCreateButton && (
              <Button
                color="dark"
                className="d-flex align-items-center"
                onClick={handleCreateClick}
              >
                <PlusIcon height={20} width={20} className="me-2" />
                New Document
              </Button>
            )
          }
        />
      </div>

      <CreateDocumentModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        creatorId={creatorId}
        campaignId={campaignId}
        onDocumentSaved={handleDocumentSaved}
      />

      {selectedDocumentId && (
        <EditDocumentModal
          documentId={selectedDocumentId}
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onDocumentSaved={handleDocumentSaved}
        />
      )}
    </div>
  );
};
